import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, Calendar, PlusCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { bookingService } from '../../services/bookingService';
import { UI, ui, buttonStyle } from '../../utils/ui';
import { formatDate } from '../../utils/helpers';

const statusColors = {
  confirmed: { backgroundColor: '#dcfce7', color: '#166534' },
  pending:   { backgroundColor: '#fef9c3', color: '#854d0e' },
  completed: { backgroundColor: '#dbeafe', color: '#1e40af' },
  cancelled: { backgroundColor: '#fee2e2', color: '#991b1b' },
};

const paymentColors = {
  paid:     { backgroundColor: '#dcfce7', color: '#166534' },
  pending:  { backgroundColor: '#fef9c3', color: '#854d0e' },
  refunded: { backgroundColor: '#e0e7ff', color: '#3730a3' },
  failed:   { backgroundColor: '#fee2e2', color: '#991b1b' },
};

const sourceColors = {
  online:  { backgroundColor: '#dbeafe', color: '#1e40af' },
  agent:   { backgroundColor: '#fef3c7', color: '#92400e' },
  offline: { backgroundColor: '#f3e8ff', color: '#6b21a8' },
};

const sourceLabels = {
  online:  'Online',
  agent:   'Agent',
  offline: 'Offline',
};

const AllBookings = () => {
  const [bookings, setBookings]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [updating, setUpdating]     = useState(null);

  useEffect(() => {
    bookingService.getAllBookings()
      .then((res) => setBookings(res.data.bookings || []))
      .catch(() => toast.error('Failed to load bookings.'))
      .finally(() => setLoading(false));
  }, []);

  const handleStatusChange = async (id, status) => {
    setUpdating(`status-${id}`);
    try {
      await bookingService.updateBookingStatus(id, status);
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, booking_status: status } : b)));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update status.');
    } finally {
      setUpdating(null);
    }
  };

  const handlePaymentChange = async (id, paymentStatus) => {
    setUpdating(`payment-${id}`);
    try {
      const res = await bookingService.updatePaymentStatus(id, paymentStatus);
      const updated = res.data.booking;
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, ...updated } : b)));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update payment status.');
    } finally {
      setUpdating(null);
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return bookings.filter((b) => {
      const matchSearch =
        (b.booking_reference || '').toLowerCase().includes(q) ||
        (b.customer_name     || '').toLowerCase().includes(q) ||
        (b.room_name         || '').toLowerCase().includes(q);
      const matchStatus = statusFilter === 'all' || b.booking_status === statusFilter;
      const matchSource = sourceFilter === 'all' || (b.source || 'online') === sourceFilter;
      return matchSearch && matchStatus && matchSource;
    });
  }, [bookings, search, statusFilter, sourceFilter]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 260 }}>
        <div style={{ width: 36, height: 36, border: `3px solid ${UI.colors.border}`, borderTopColor: UI.colors.primary, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  const fieldCore = { width: '100%', border: 'none', outline: 'none', fontSize: 14, backgroundColor: 'transparent', color: UI.colors.text };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: UI.font.heading, fontSize: 30, fontWeight: 900, margin: 0, color: UI.colors.text }}>All Bookings</h1>
          <p style={{ margin: '8px 0 0', fontSize: 14, color: UI.colors.muted }}>{bookings.length} total bookings</p>
        </div>
        <Link to="/owner/create-booking" style={{ ...buttonStyle('primary'), textDecoration: 'none' }}>
          <PlusCircle size={16} />
          New Booking
        </Link>
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 240, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 14, border: `1.5px solid ${UI.colors.border}`, backgroundColor: '#ffffff' }}>
          <Search size={16} color="#9ca3af" />
          <input
            type="text"
            placeholder="Search by ref, customer or room..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={fieldCore}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 14, border: `1.5px solid ${UI.colors.border}`, backgroundColor: '#ffffff' }}>
          <Filter size={16} color="#9ca3af" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ border: 'none', outline: 'none', backgroundColor: 'transparent', fontSize: 14, fontWeight: 800, color: UI.colors.text, cursor: 'pointer' }}>
            <option value="all">All Status</option>
            <option value="confirmed">Confirmed</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 14, border: `1.5px solid ${UI.colors.border}`, backgroundColor: '#ffffff' }}>
          <Filter size={16} color="#9ca3af" />
          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} style={{ border: 'none', outline: 'none', backgroundColor: 'transparent', fontSize: 14, fontWeight: 800, color: UI.colors.text, cursor: 'pointer' }}>
            <option value="all">All Sources</option>
            <option value="online">Online</option>
            <option value="agent">Agent</option>
            <option value="offline">Offline</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div style={ui.card}>
        <div style={{ padding: 22 }}>
          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '56px 18px', color: UI.colors.muted }}>
              <Calendar size={44} color="#9ca3af" style={{ opacity: 0.3, margin: '0 auto 12px' }} />
              <p style={{ margin: 0, fontWeight: 900 }}>No bookings found</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: 1020 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 0.9fr 0.8fr 0.7fr 1fr 1fr', gap: 10, padding: '10px 12px', borderRadius: 14, backgroundColor: '#f3f4f6', border: `1px solid ${UI.colors.border}`, fontSize: 11, fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9ca3af', marginBottom: 10 }}>
                  <span>Reference</span><span>Customer</span><span>Room</span><span>Dates</span><span>Amount</span><span>Source</span><span>Status</span><span>Payment</span>
                </div>
                {filtered.map((b) => {
                  const src = b.source || 'online';
                  const srcStyle = sourceColors[src] || sourceColors.online;
                  return (
                    <div key={b.id} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 0.9fr 0.8fr 0.7fr 1fr 1fr', gap: 10, padding: '12px 12px', borderBottom: `1px solid ${UI.colors.border}`, alignItems: 'center', fontSize: 13 }}>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ margin: 0, color: UI.colors.primary, fontWeight: 900, fontSize: 12 }}>{b.booking_reference}</p>
                        <p style={{ margin: '4px 0 0', fontSize: 12, color: '#9ca3af', fontWeight: 800 }}>{b.agent_name ? `via ${b.agent_name}` : ''}</p>
                      </div>
                      <span style={{ color: UI.colors.text, fontWeight: 700 }}>{b.customer_name}</span>
                      <span style={{ color: UI.colors.text, fontWeight: 700 }}>{b.room_name}</span>
                      <div style={{ fontSize: 12, color: UI.colors.muted, fontWeight: 800, lineHeight: 1.35 }}>
                        {formatDate(b.check_in, 'dd-MMM-yyyy')}<br />{formatDate(b.check_out, 'dd-MMM-yyyy')}
                      </div>
                      <span style={{ fontWeight: 900, color: UI.colors.text }}>₹{Number(b.total_amount).toLocaleString()}</span>
                      <span style={{ padding: '3px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700, ...srcStyle }}>
                        {sourceLabels[src] || src}
                      </span>
                      <select
                        style={{ padding: '6px 10px', border: `1px solid ${UI.colors.border}`, borderRadius: 9999, fontSize: 12, fontWeight: 900, cursor: 'pointer', outline: 'none', ...(statusColors[b.booking_status] || statusColors.pending) }}
                        value={b.booking_status}
                        onChange={(e) => handleStatusChange(b.id, e.target.value)}
                        disabled={updating === `status-${b.id}` || b.booking_status === 'cancelled'}
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                      <select
                        style={{ padding: '6px 10px', border: `1px solid ${UI.colors.border}`, borderRadius: 9999, fontSize: 12, fontWeight: 900, cursor: 'pointer', outline: 'none', ...(paymentColors[b.payment_status] || paymentColors.pending) }}
                        value={b.payment_status || 'pending'}
                        onChange={(e) => handlePaymentChange(b.id, e.target.value)}
                        disabled={updating === `payment-${b.id}`}
                      >
                        <option value="pending">Pending</option>
                        <option value="paid">Paid</option>
                        <option value="refunded">Refunded</option>
                        <option value="failed">Failed</option>
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AllBookings;
